import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { checkAuthAndRedirect } from "../../../utils/auth";
import { decodeTestNameFromPath, useTestRoutes } from "./testRoutes";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { BsClock } from "react-icons/bs";
import { VscListFlat } from "react-icons/vsc";
import { GiStarShuriken } from "react-icons/gi";
import { MdDeleteForever } from "react-icons/md";
import { BiSolidEdit } from "react-icons/bi";
import { RiListCheck3 } from "react-icons/ri";
import { ImListNumbered } from "react-icons/im";
import { LuPlus } from "react-icons/lu";
import TestFilterSelect from "./TestFilterSelect";
import authService from "../../../utils/authService";
import {
    fetchTopicsForSubject,
    type ContentTopic,
} from "../../../utils/contentUtils";
import { fetchTestSubjects } from "./testUtils";
import type { SelectedSubject } from "./testTypes";

type TestData = {
    qn_id: string | "";
    qn_name: string;
    question_type: string;
    level: string;
    question: number;
    score: string | null;
    time: string | null;
};

type ResponseTestData = {
    test: string;
    hours: number;
    minutes: number;
    description: string;
    marks: number;
    duration: number;
};

type Section = {
    id: number;
    questionType: string;
    selectedQuestions: TestData[];
    isViewing: boolean;
    questionTypeLocked: boolean;
};

const QUESTION_TYPES = [
    { value: "MCQ", label: "MCQ" },
    { value: "Coding", label: "Coding" },
];

const sumTime = (questions: TestData[]) =>
    questions.reduce((total, q) => total + parseInt(q.time || "0", 10), 0);

const sumScore = (questions: TestData[]) =>
    questions.reduce((total, q) => total + parseInt(q.score || "0", 10), 0);

const plural = (count: number, singular: string, pluralForm: string) =>
    count === 1 ? singular : pluralForm;

const excludeSelectedQuestions = (list: TestData[], sections: Section[]) =>
    list.filter(
        (test) =>
            !sections.some((section) =>
                section.selectedQuestions.some((q) => q.qn_id === test.qn_id)
            )
    );

type QuestionMetaProps = { test: TestData };

const QuestionMeta: React.FC<QuestionMetaProps> = ({ test }) => {
    const mins = parseInt(test.time || "0", 10);
    const marks = parseInt(test.score || "0", 10);
    return (
        <div className="d-flex flex-wrap gap-3 small mb-2">
            <span>
                <RiListCheck3 className="me-1" />
                {test.question_type || "N/A"}
            </span>
            <span>
                <ImListNumbered className="me-1" />
                {test.level || "N/A"}
            </span>
            <span>
                <BsClock className="me-1" />
                {mins} {plural(mins, "Minute", "Minutes")}
            </span>
            <span>
                <GiStarShuriken className="me-1" />
                {marks} {plural(marks, "Mark", "Marks")}
            </span>
        </div>
    );
};

type QuestionCardProps = {
    test: TestData;
    action?: React.ReactNode;
};

const QuestionCard: React.FC<QuestionCardProps> = ({ test, action }) => (
    <div className="card shadow-sm mb-3">
        <div className="card-body py-2">
            <div className="d-flex justify-content-between align-items-start gap-2">
                <div className="flex-grow-1 text-break">
                    <div className="fw-semibold">{test.qn_name || "Test Title"}</div>
                    <QuestionMeta test={test} />
                </div>
                {action}
            </div>
            <div className="text-break">
                <div>{test.question}</div>
            </div>
        </div>
    </div>
);

type TestCreationLocationState = {
    test_id?: string;
    isClone?: boolean;
    old_test_id?: string;
};

const TestCreation: React.FC = () => {
    const { testName: testNameParam } = useParams<{ testName: string }>();
    const [searchParams] = useSearchParams();
    const { state } = useLocation();
    const locationState = state as TestCreationLocationState | null;
    const routeTestName = decodeTestNameFromPath(testNameParam);
    const test_id = searchParams.get("test_id") ?? locationState?.test_id;
    const isClone =
        searchParams.get("is_clone") === "true" || locationState?.isClone === true;
    const old_test_id = searchParams.get("old_test_id") ?? locationState?.old_test_id;
    const [responseTestData, setResponseTestData] = useState<ResponseTestData | null>(null);
    const [assignSubjects, setAssignSubjects] = useState<SelectedSubject[]>([]);
    const [assignSubjectsLoading, setAssignSubjectsLoading] = useState(false);
    const [assignTopics, setAssignTopics] = useState<ContentTopic[]>([]);
    const [assignTopicsLoading, setAssignTopicsLoading] = useState(false);
    /** Locked from test create — not shown in UI */
    const [selectedTrack, setSelectedTrack] = useState("");
    const [selectedSubject, setSelectedSubject] = useState("");
    const [selectedTopic, setSelectedTopic] = useState("");
    const [originalTests, setOriginalTests] = useState<TestData[]>([]);
    const [displayTests, setDisplayTests] = useState<TestData[]>([]);
    const [sections, setSections] = useState<Section[]>([]);
    const [viewAll, setViewAll] = useState(false);
    const [activeSelectionSectionId, setActiveSelectionSectionId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [activeButton, setActiveButton] = useState<string | null>(null);
    const [editedTestName, setEditedTestName] = useState("");
    const [editedHours, setEditedHours] = useState(0);
    const [editedMinutes, setEditedMinutes] = useState(0);
    const [editedMarks, setEditedMarks] = useState(0);
    const [editedDescription, setEditedDescription] = useState("");

    const navigate = useNavigate();
    const routes = useTestRoutes();

    const totalSelectedMinutes = useMemo(
        () => sumTime(sections.flatMap((s) => s.selectedQuestions)),
        [sections]
    );
    const totalSelectedMarks = useMemo(
        () => sumScore(sections.flatMap((s) => s.selectedQuestions)),
        [sections]
    );
    const viewingSection = sections.find((s) => s.isViewing);

    const showMessage = (message: string) => {
        setModalMessage(message);
        setShowModal(true);
    };

    const refreshDisplayTests = (
        nextSections: Section[],
        sectionId: number | null = activeSelectionSectionId,
        typeFilter?: string
    ) => {
        let list = originalTests;
        if (typeFilter) {
            list = list.filter((test) => test.question_type === typeFilter);
        } else if (sectionId !== null) {
            const section = nextSections.find((s) => s.id === sectionId);
            if (section?.questionType) {
                list = list.filter((test) => test.question_type === section.questionType);
            }
        }
        setDisplayTests(excludeSelectedQuestions(list, nextSections));
    };

    useEffect(() => {
        if (!routeTestName || !test_id) {
            navigate(routes.create, { replace: true });
        }
    }, [routeTestName, test_id, navigate, routes.create]);

    useEffect(() => {
        if (!test_id) return;
        if (!checkAuthAndRedirect()) return;
        let cancelled = false;
        const fetchTestData = async () => {
            setLoading(true);
            setAssignSubjectsLoading(true);
            try {
                sessionStorage.setItem("TestID", test_id);
                const [testResponse, subjectsResult] = await Promise.all([
                    authService.get(`/admin/tests/get/${test_id}`),
                    fetchTestSubjects(test_id),
                ]);
                if (cancelled) return;

                const data = testResponse.data;
                setResponseTestData(data);

                const totalMinutes = data.duration;
                setEditedHours(Math.floor(totalMinutes / 60));
                setEditedMinutes(totalMinutes % 60);
                setEditedTestName(data.test);
                setEditedMarks(data.marks);
                setEditedDescription(data.description);

                // Track is fixed at create time; subjects are only those chosen for this test
                setSelectedTrack(subjectsResult.track_id ?? "");
                setAssignSubjects(subjectsResult.subjects);
                setSelectedSubject(
                    subjectsResult.subjects.length === 1
                        ? subjectsResult.subjects[0].subject_id
                        : ""
                );
                setSelectedTopic("");
                setAssignTopics([]);
            } catch (error) {
                console.error("Error fetching test data:", error);
                if (!cancelled) {
                    setAssignSubjects([]);
                    setSelectedTrack("");
                    setSelectedSubject("");
                    setSelectedTopic("");
                    setAssignTopics([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    setAssignSubjectsLoading(false);
                }
            }
        };
        fetchTestData();
        return () => {
            cancelled = true;
        };
    }, [test_id]);

    useEffect(() => {
        if (!selectedSubject) {
            setAssignTopics([]);
            return;
        }
        let cancelled = false;
        (async () => {
            setAssignTopicsLoading(true);
            try {
                const topics = await fetchTopicsForSubject(selectedSubject);
                if (!cancelled) setAssignTopics(topics);
            } catch (err) {
                console.error("Failed to fetch topics:", err);
                if (!cancelled) setAssignTopics([]);
            } finally {
                if (!cancelled) setAssignTopicsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [selectedSubject]);

    useEffect(() => {
        if (!isClone || !old_test_id || !test_id) return;
        const fetchClonedSections = async () => {
            setLoading(true);
            try {
                const response = await authService.get(
                    `/admin/test/sections/clone/${old_test_id}/`
                );
                const clonedSections: Section[] = response.data.sections.map((section: any) => ({
                    id: section.section_number,
                    questionType: section.section_name,
                    selectedQuestions: section.questions.map((q: any) => ({
                        qn_id: q.qn_id || "",
                        qn_name: q.qn_name || "",
                        question_type: q.question_type || "",
                        level: q.level || "",
                        question: q.question || 0,
                        score: q.score?.toString() || null,
                        time: q.time?.toString() || null,
                    })),
                    isViewing: true,
                    questionTypeLocked: true,
                }));
                setSections(clonedSections);
                setViewAll(true);
                setActiveButton("finalView");
            } catch (error) {
                console.error("Error fetching cloned sections:", error);
                showMessage("Error loading cloned sections");
            } finally {
                setLoading(false);
            }
        };
        fetchClonedSections();
    }, [isClone, old_test_id, test_id]);

    useEffect(() => {
        if (!selectedTopic) {
            setOriginalTests([]);
            setDisplayTests([]);
            return;
        }
        const fetchQuestions = async () => {
            setLoading(true);
            try {
                const testsResponse = await authService.post("/admin/tests/questions/", {
                    track_id: selectedTrack,
                    subject_id: selectedSubject,
                    topic_id: selectedTopic,
                });
                const questionsData = testsResponse.data.questions || [];
                setOriginalTests(questionsData);
                setDisplayTests(excludeSelectedQuestions(questionsData, sections));
            } catch (error) {
                console.error("Error fetching questions:", error);
                setOriginalTests([]);
                setDisplayTests([]);
            } finally {
                setLoading(false);
            }
        };
        fetchQuestions();
    }, [
        selectedTopic,
        selectedTrack,
        selectedSubject,
    ]);

    useEffect(() => {
        if (viewAll || sections.some((s) => s.isViewing)) return;
        refreshDisplayTests(sections);
    }, [sections, activeSelectionSectionId, originalTests, viewAll]);

    const addSection = () => {
        const lastSection = sections[sections.length - 1];
        if (lastSection && lastSection.selectedQuestions.length === 0) {
            showMessage("Please add questions to the current section before adding a new one.");
            return;
        }
        setSections([
            ...sections,
            {
                id: sections.length + 1,
                questionType: "",
                selectedQuestions: [],
                isViewing: false,
                questionTypeLocked: false,
            },
        ]);
    };

    const handleCheckboxChange = (testItem: TestData) => {
        if (activeSelectionSectionId === null) {
            showMessage(
                "Please click 'Select' on a section first to determine where to add questions"
            );
            return;
        }

        const activeSection = sections.find((s) => s.id === activeSelectionSectionId);
        const isSelected = activeSection?.selectedQuestions.some((q) => q.qn_id === testItem.qn_id);

        const nextSections = sections.map((section) =>
            section.id === activeSelectionSectionId
                ? {
                      ...section,
                      selectedQuestions: isSelected
                          ? section.selectedQuestions.filter((q) => q.qn_id !== testItem.qn_id)
                          : [...section.selectedQuestions, testItem],
                  }
                : section
        );
        setSections(nextSections);
        refreshDisplayTests(nextSections);
    };

    const handleSelect = (sectionId: number) => {
        setActiveSelectionSectionId(sectionId);
        setActiveButton("select");
        setViewAll(false);
        const nextSections = sections.map((section) => ({ ...section, isViewing: false }));
        setSections(nextSections);
        refreshDisplayTests(nextSections, sectionId);
    };

    const handleView = (sectionId: number) => {
        setActiveSelectionSectionId(sectionId);
        setActiveButton("view");
        setViewAll(false);
        setSections(
            sections.map((section) => ({
                ...section,
                isViewing: section.id === sectionId,
            }))
        );
    };

    const handleFinalView = () => {
        setViewAll(true);
        setActiveButton("finalView");
        setSections(sections.map((section) => ({ ...section, isViewing: false })));
        setDisplayTests(originalTests);
    };

    const handleFinalSave = async () => {
        setLoading(true);
        const payload = {
            test_id,
            sections: sections.map((section) => ({
                section_name: `Section${section.id}`,
                section_type: section.questionType,
                qn_list: section.selectedQuestions.map((q) => q.qn_id),
            })),
        };
        try {
            const saveResponse = await authService.post(
                "/admin/tests/sections/",
                payload
            );
            const computedDuration = saveResponse.data?.duration;
            const computedMarks = saveResponse.data?.marks;
            const summary =
                computedDuration != null && computedMarks != null
                    ? ` Sections saved. Duration: ${computedDuration} min, Marks: ${computedMarks}.`
                    : " Sections saved successfully!";
            showMessage(summary.trim());
            navigate(routes.configure);
        } catch (error) {
            console.error("Error saving sections:", error);
            showMessage("Error saving sections");
        } finally {
            setLoading(false);
        }
    };

    const handleQuestionTypeDropdownChange = (value: string, sectionId: number) => {
        setViewAll(false);
        const nextSections = sections.map((section) =>
            section.id === sectionId
                ? { ...section, questionType: value, questionTypeLocked: true }
                : section
        );
        setSections(nextSections);
        refreshDisplayTests(nextSections, sectionId, value);
    };

    const handleUpdateTest = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!editedTestName.trim()) {
            showMessage("Test name is required.");
            return;
        }

        setLoading(true);
        try {
            const resolvedTestId = test_id || sessionStorage.getItem("TestID");
            if (!resolvedTestId) {
                showMessage("Unable to update test. Missing test id.");
                return;
            }

            await authService.put("/admin/", {
                test_id: resolvedTestId,
                test_name: editedTestName,
                description: editedDescription,
                created_by: sessionStorage.getItem("Email"),
            });

            const testResponse = await authService.get(`/admin/tests/get/${test_id}`);
            const data = testResponse.data;
            setResponseTestData(data);

            const totalMinutes = data.duration;
            setEditedHours(Math.floor(totalMinutes / 60));
            setEditedMinutes(totalMinutes % 60);
            setEditedTestName(data.test);
            setEditedMarks(data.marks);
            setEditedDescription(data.description);
            setShowUpdateModal(false);
        } catch (error) {
            console.error("Error updating test:", error);
            showMessage("Error updating test");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSection = (sectionId: number) => {
        const nextSections = sections
            .filter((section) => section.id !== sectionId)
            .map((section, index) => ({ ...section, id: index + 1 }));
        setSections(nextSections);
        if (activeSelectionSectionId === sectionId) {
            setActiveSelectionSectionId(null);
        }
        refreshDisplayTests(nextSections, activeSelectionSectionId === sectionId ? null : activeSelectionSectionId);
    };

    const handleDeleteQuestionFromSection = (sectionId: number, questionId: string) => {
        const nextSections = sections.map((section) =>
            section.id === sectionId
                ? {
                      ...section,
                      selectedQuestions: section.selectedQuestions.filter(
                          (q) => q.qn_id !== questionId
                      ),
                  }
                : section
        );
        setSections(nextSections);
        refreshDisplayTests(nextSections);
    };

    const sectionActionBtn = (label: string, mode: string, sectionId: number, onClick: () => void) => (
        <button
            type="button"
            className={`btn btn-sm w-100 ${
                activeButton === mode && sectionId === activeSelectionSectionId
                    ? "btn-success"
                    : "btn-light"
            }`}
            onClick={onClick}
        >
            {label}
        </button>
    );

    const renderQuestionList = () => {
        if (viewAll) {
            return sections.map((section) => (
                <div key={section.id} className="mb-3">
                    <div className="card shadow-sm mb-2">
                        <div className="card-body py-2">
                            <div className="fw-semibold">Section {section.id}</div>
                            <div className="text-muted">{section.questionType}</div>
                        </div>
                    </div>
                    {section.selectedQuestions.map((test) => (
                        <QuestionCard key={test.qn_id} test={test} />
                    ))}
                </div>
            ));
        }

        if (viewingSection) {
            return (
                <div>
                    <h5 className="mb-3">
                        Section {viewingSection.id} {viewingSection.questionType} Questions
                    </h5>
                    {viewingSection.selectedQuestions.map((test) => (
                        <QuestionCard
                            key={test.qn_id}
                            test={test}
                            action={
                                <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() =>
                                        handleDeleteQuestionFromSection(viewingSection.id, test.qn_id)
                                    }
                                >
                                    <MdDeleteForever size={20} />
                                </button>
                            }
                        />
                    ))}
                </div>
            );
        }

        return displayTests.map((test) => (
            <QuestionCard
                key={test.qn_id}
                test={test}
                action={
                    <Form.Check
                        type="checkbox"
                        checked={sections.some(
                            (section) =>
                                section.id === activeSelectionSectionId &&
                                section.selectedQuestions.some((q) => q.qn_id === test.qn_id)
                        )}
                        onChange={() => handleCheckboxChange(test)}
                    />
                }
            />
        ));
    };

    if (!routeTestName || !test_id) {
        return null;
    }

    return (
        <div
            className="border rounded bg-white my-2 me-2 d-flex flex-column"
            style={{ height: "calc(100vh - 85px)" }}
        >
            <div className="d-flex flex-grow-1" style={{ minHeight: 0 }}>
                <div
                    className="border-end overflow-y-auto p-2 flex-shrink-0"
                    style={{ minHeight: 0, minWidth: "350px" }}
                >
                    <div className="card shadow-sm mb-2">
                        <div className="card-body d-flex flex-column gap-2 py-2">
                            <TestFilterSelect
                                label="Subject"
                                value={selectedSubject}
                                onChange={(value) => {
                                    setSelectedSubject(value);
                                    setSelectedTopic("");
                                }}
                                disabled={
                                    assignSubjectsLoading ||
                                    assignSubjects.length === 0
                                }
                                placeholder={
                                    assignSubjectsLoading
                                        ? "Loading subjects..."
                                        : assignSubjects.length === 0
                                          ? "No subjects on this test"
                                          : "Select Subject"
                                }
                                options={assignSubjects.map((subject) => ({
                                    value: subject.subject_id,
                                    label: subject.subject_name,
                                }))}
                                fullWidth
                            />
                            <TestFilterSelect
                                label="Topic"
                                value={selectedTopic}
                                onChange={setSelectedTopic}
                                disabled={
                                    !selectedSubject ||
                                    assignTopicsLoading ||
                                    assignTopics.length === 0
                                }
                                placeholder="Select Topic"
                                options={assignTopics.map((topic) => ({
                                    value: topic.topic_id,
                                    label: topic.topic_name,
                                }))}
                                fullWidth
                            />
                        </div>
                    </div>

                    <div className="card shadow-sm mb-2">
                        <div className="card-body py-2 d-flex justify-content-between align-items-center gap-2 small">
                            <span className="text-nowrap">
                                <BsClock className="me-1" />
                                Selected: {totalSelectedMinutes}{" "}
                                {plural(totalSelectedMinutes, "Minute", "Minutes")}
                            </span>
                            <span className="text-nowrap">
                                <GiStarShuriken className="me-1" />
                                Selected: {totalSelectedMarks}{" "}
                                {plural(totalSelectedMarks, "Mark", "Marks")}
                            </span>
                        </div>
                    </div>

                    <div className="d-flex justify-content-end mb-2">
                        <button type="button" className="btn btn-light btn-sm" onClick={addSection}>
                            <LuPlus className="me-1" />
                            Add Section
                        </button>
                    </div>

                    {sections.map((section) => {
                        const sectionMins = sumTime(section.selectedQuestions);
                        const sectionMarks = sumScore(section.selectedQuestions);
                        const isActive =
                            section.id === activeSelectionSectionId && activeButton !== "finalView";

                        return (
                            <div
                                key={section.id}
                                className={`card shadow-sm mb-2 ${isActive ? "border-primary" : ""}`}
                            >
                                <div className="card-body py-2">
                                    <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
                                        <div className="d-flex gap-3 small">
                                            <span>
                                                <BsClock className="me-1" />
                                                {sectionMins} {plural(sectionMins, "Minute", "Minutes")}
                                            </span>
                                            <span>
                                                <GiStarShuriken className="me-1" />
                                                {sectionMarks} {plural(sectionMarks, "Mark", "Marks")}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-light btn-sm p-1"
                                            onClick={() => handleDeleteSection(section.id)}
                                        >
                                            <MdDeleteForever className="text-danger" size={18} />
                                        </button>
                                    </div>

                                    <div className="d-flex justify-content-between gap-2 flex-wrap">
                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                            <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                                                <span className="badge bg-light text-dark border">
                                                    Section {section.id}
                                                </span>
                                                <TestFilterSelect
                                                    label="Type"
                                                    value={section.questionType}
                                                    onChange={(value) =>
                                                        handleQuestionTypeDropdownChange(value, section.id)
                                                    }
                                                    disabled={section.questionTypeLocked}
                                                    placeholder="Type"
                                                    options={QUESTION_TYPES}
                                                    minWidth={120}
                                                />
                                            </div>
                                            <div className="fw-semibold">
                                                {section.selectedQuestions.length}{" "}
                                                {plural(
                                                    section.selectedQuestions.length,
                                                    "Question",
                                                    "Questions"
                                                )}{" "}
                                                added
                                            </div>
                                        </div>
                                        <div className="d-flex flex-column gap-1" style={{ width: 72 }}>
                                            {sectionActionBtn("Select", "select", section.id, () =>
                                                handleSelect(section.id)
                                            )}
                                            {sectionActionBtn("View", "view", section.id, () =>
                                                handleView(section.id)
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    <div className="card shadow-sm">
                        <div className="card-body py-2 d-flex justify-content-center gap-2">
                            <button
                                type="button"
                                className={`btn btn-sm ${
                                    activeButton === "finalView" ? "btn-success" : "btn-light"
                                }`}
                                onClick={handleFinalView}
                            >
                                View
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-light"
                                onClick={handleFinalSave}
                                disabled={sections.length === 0}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>

                <div
                    className="flex-grow-1 overflow-y-auto p-2"
                    style={{ minHeight: 0, minWidth: 0 }}
                >
                    <div className="card shadow-sm mb-2 bg-light">
                        <div className="card-body py-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="fw-semibold">
                                    <VscListFlat className="me-2" />
                                    {responseTestData?.test ?? routeTestName}
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-link p-0"
                                    onClick={() => setShowUpdateModal(true)}
                                >
                                    <BiSolidEdit size={22} />
                                </button>
                            </div>
                            {responseTestData?.description && (
                                <div className="text-muted mt-1">{responseTestData.description}</div>
                            )}
                        </div>
                    </div>
                    {renderQuestionList()}
                </div>
            </div>

            {loading && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75 z-3">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Message</Modal.Title>
                </Modal.Header>
                <Modal.Body>{modalMessage}</Modal.Body>
            </Modal>

            <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)} size="lg" centered backdrop="static" keyboard={false}>
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title className="fw-semibold">Edit Test</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleUpdateTest} className="p-2">
                        <Form.Group controlId="testName" className="mb-3">
                            <Form.Label className="fw-semibold">Test Name *</Form.Label>
                            <Form.Control
                                type="text"
                                value={editedTestName}
                                onChange={(e) => setEditedTestName(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <div className="d-flex flex-wrap gap-4 mb-3 small text-muted">
                            <span>
                                <BsClock className="me-1" />
                                Duration (from questions): {editedHours}h {editedMinutes}m
                            </span>
                            <span>
                                <GiStarShuriken className="me-1" />
                                Marks (from questions): {editedMarks}
                            </span>
                        </div>
                        <Form.Group controlId="description" className="mb-3">
                            <Form.Label className="fw-semibold">Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                value={editedDescription}
                                onChange={(e) => setEditedDescription(e.target.value)}
                                placeholder="Write here..."
                                rows={4}
                            />
                        </Form.Group>
                        <Modal.Footer className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={() => setShowUpdateModal(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={loading}>
                                {loading ? <Spinner animation="border" size="sm" /> : "Update"}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default TestCreation;
